import { useState, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest } from './authConfig';
import { LICENSE_MANAGEMENT_API_ADDRESS, STRIPE_BILLING_PORTAL_URL } from './apiConfig';

const ROLES = [
  [4096, 'Guest'],
  [512,  'User'],
  [64,   'InternalAdmin'],
  [8,    'Admin'],
  [1,    'Owner'],
];

function formatRole(value) {
  if (value === 0) return 'None';
  return ROLES.filter(([flag]) => (value & flag) !== 0).map(([, name]) => name).join(', ');
}

const ASSIGNABLE_ROLES = [
  { value: 8,    label: 'Admin' },
  { value: 64,   label: 'InternalAdmin' },
  { value: 512,  label: 'User' },
  { value: 4096, label: 'Guest' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = email => EMAIL_RE.test(email);

function getRoleOptions(currentRole) {
  if (ASSIGNABLE_ROLES.some(r => r.value === currentRole)) return ASSIGNABLE_ROLES;
  return [{ value: currentRole, label: formatRole(currentRole) }, ...ASSIGNABLE_ROLES];
}

let _keySeq = 0;
const nextKey = () => String(++_keySeq);

function withKeys(list) {
  return list.map(u => ({ ...u, _key: u.UserId != null ? String(u.UserId) : nextKey() }));
}

function AdminPage({ orders }) {
  const { instance, accounts } = useMsal();
  const [selectedOrderId, setSelectedOrderId] = useState(orders[0]?.Id ?? null);
  const [users, setUsers] = useState([]);
  const [committedUsers, setCommittedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });

  useEffect(() => {
    if (selectedOrderId === null) return;
    let cancelled = false;
    const fetchUsers = async () => {
      setLoadingUsers(true);
      const account = instance.getActiveAccount() || accounts[0];
      try {
        const { accessToken } = await instance.acquireTokenSilent({
          scopes: apiRequest.scopes,
          account,
        });
        const res = await fetch(`${LICENSE_MANAGEMENT_API_ADDRESS}?operation=get_users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ OrderId: selectedOrderId }),
        });
        if (cancelled) return;
        if (!res.ok) { setUsers([]); setCommittedUsers([]); return; }
        const data = await res.json();
        const list = withKeys(Array.isArray(data) ? data : []);
        setUsers(list);
        setCommittedUsers(list);
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ scopes: apiRequest.scopes, account });
          return;
        }
        if (!cancelled) { setUsers([]); setCommittedUsers([]); }
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    };
    fetchUsers();
    return () => { cancelled = true; };
  }, [selectedOrderId, instance, accounts]);

  const selectedOrder = orders.find(o => o.Id === selectedOrderId);
  const seatCount = selectedOrder?.SeatCount ?? 0;

  const hasChanges = useMemo(() => {
    const norm = list => JSON.stringify(list.map(u => ({ Email: u.Email, Role: u.Role })));
    return norm(users) !== norm(committedUsers);
  }, [users, committedUsers]);

  const hasErrors = users.some(u => !isValidEmail(u.Email));

  const handleAddUser = () => {
    setUsers(prev => [...prev, { _key: nextKey(), Email: '', Role: 512 }]);
  };

  const handleRemoveUser = index => {
    setUsers(prev => prev.filter((_, i) => i !== index));
  };

  const handleEmailChange = (index, value) => {
    setUsers(prev => prev.map((u, i) => i === index ? { ...u, Email: value } : u));
  };

  const handleRoleChange = (index, value) => {
    setUsers(prev => prev.map((u, i) => i === index ? { ...u, Role: Number(value) } : u));
  };

  const handleSort = column => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedUsers = useMemo(() => {
    const indexed = users.map((user, originalIndex) => ({ user, originalIndex }));
    if (!sortConfig.column) return indexed;
    return [...indexed].sort((a, b) => {
      const aVal = sortConfig.column === 'Email'
        ? a.user.Email.toLowerCase()
        : formatRole(a.user.Role).toLowerCase();
      const bVal = sortConfig.column === 'Email'
        ? b.user.Email.toLowerCase()
        : formatRole(b.user.Role).toLowerCase();
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [users, sortConfig]);

  // Ask the License Management API to create a Stripe billing portal session
  // for this order's customer, then send the user there to review payments and
  // cancel/update subscriptions.
  const handleManageSubscriptions = async () => {
    if (selectedOrderId === null) return;
    const account = instance.getActiveAccount() || accounts[0];
    setOpeningPortal(true);
    try {
      const { accessToken } = await instance.acquireTokenSilent({
        scopes: apiRequest.scopes,
        account,
      });
      // TODO: confirm the operation name once the endpoint exists. Expected to
      // create a Stripe billing portal session (server-side, with a return_url
      // back to this page) scoped to the order's customer and return its URL.
      // TODO: confirm OrderId is the right key to scope the Stripe session — the
      // endpoint may instead need a Stripe customer/subscription id carried on
      // the order rather than the OrderId itself.
      const res = await fetch(`${LICENSE_MANAGEMENT_API_ADDRESS}?operation=get_billing_portal_url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ OrderId: selectedOrderId }),
      });
      if (!res.ok) throw new Error(`Billing portal request failed (${res.status})`);
      const data = await res.json();
      // TODO: confirm the response field name (e.g. Url / PortalUrl) and whether
      // the body is double-encoded JSON like the Products API responses.
      const portalUrl = data?.Url;
      // TODO: remove this fallback once the endpoint is live — until then the
      // API 404s and we send the user to the generic Stripe portal.
      window.location.assign(portalUrl || STRIPE_BILLING_PORTAL_URL);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ scopes: apiRequest.scopes, account });
        return;
      }
      // TODO: surface this failure in the UI (toast/inline message).
      window.location.assign(STRIPE_BILLING_PORTAL_URL);
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleCancel = () => setUsers(committedUsers);

  const handleSave = async () => {
    const account = instance.getActiveAccount() || accounts[0];
    try {
      const { accessToken } = await instance.acquireTokenSilent({
        scopes: apiRequest.scopes,
        account,
      });
      const res = await fetch(`${LICENSE_MANAGEMENT_API_ADDRESS}?operation=set_users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          OrderId: selectedOrderId,
          Users: users.map(u => ({ Email: u.Email, Role: u.Role })),
        }),
      });
      if (res.ok) setCommittedUsers(users);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenPopup({ scopes: apiRequest.scopes, account });
      }
    }
  };

  return (
    <div className="page">
      <h1 className="page-title">Products and Users Admin</h1>

      <div className="selector-row">
        <label htmlFor="order-select">Order</label>
        <select
          id="order-select"
          className="input"
          value={selectedOrderId ?? ''}
          onChange={e => setSelectedOrderId(Number(e.target.value))}
        >
          {orders.map(order => (
            <option key={order.Id} value={order.Id}>
              {order.ProductName} — #{order.Id}
            </option>
          ))}
        </select>
      </div>

      {selectedOrder && (
        <div className="meta-panel">
          <span><strong>Seats:</strong> {selectedOrder.SeatCount}</span>
          <span>
            <strong>{selectedOrder.Active ? 'Auto Renews on:' : 'Expires on:'}</strong>{' '}
            {new Date(selectedOrder.Expiration).toLocaleDateString()}
          </span>
          <span><strong>Transaction:</strong> {selectedOrder.TransactionId}</span>
          <button
            type="button"
            className="btn btn--outlined meta-panel-action"
            onClick={handleManageSubscriptions}
            disabled={openingPortal}
          >
            {openingPortal ? 'Opening…' : 'Manage Subscriptions'}
          </button>
        </div>
      )}

      {loadingUsers ? (
        <p className="loading-text">Loading users…</p>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                {['Email', 'Role'].map(col => (
                  <th
                    key={col}
                    className="th-sortable"
                    onClick={() => handleSort(col)}
                    aria-sort={
                      sortConfig.column === col
                        ? sortConfig.direction === 'asc' ? 'ascending' : 'descending'
                        : 'none'
                    }
                  >
                    {col}
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={3} className="table-empty">No users found for this order.</td>
                </tr>
              ) : (
                sortedUsers.map(({ user, originalIndex }) => (
                  <tr key={user._key}>
                    <td>
                      <input
                        type="email"
                        value={user.Email}
                        onChange={e => handleEmailChange(originalIndex, e.target.value)}
                        className={`input${!isValidEmail(user.Email) ? ' input--invalid' : ''}`}
                        placeholder="user@example.com"
                      />
                    </td>
                    <td>
                      <select
                        value={user.Role}
                        onChange={e => handleRoleChange(originalIndex, e.target.value)}
                        className="input"
                      >
                        {getRoleOptions(user.Role).map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="table-actions-cell">
                      <button className="btn btn--danger" onClick={() => handleRemoveUser(originalIndex)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="table-footer">
            <button
              className="btn btn--outlined"
              onClick={handleAddUser}
              disabled={users.length >= seatCount}
            >
              + Add User
            </button>
            <div className="table-footer-actions">
              <button className="btn btn--outlined" onClick={handleCancel} disabled={!hasChanges}>
                Cancel
              </button>
              <button className="btn btn--filled" onClick={handleSave} disabled={!hasChanges || hasErrors}>
                Save
              </button>
            </div>
          </div>
          <p className="note note--spaced">Please allow up to one hour for changes to take effect after saving.</p>
        </>
      )}
    </div>
  );
}

export default AdminPage;
